import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Calendar, Clock } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { getJson, peekEarlyData } from "../utils/earlyData";
import LeadPicture from "../components/LeadPicture";
import ArticleShareRow from "../components/ArticleShareRow";
import ArticleSidebar from "../components/ArticleSidebar";
import BannerSlot from "../components/BannerSlot";
import SEO, { ORGANIZATION } from "../components/SEO";
import ArticleBody from "../components/ArticleBody";
import { optimizeImage } from "../utils/optimizeImage";
import ReadNext from "../components/ReadNext";
import InlineSubscribe from "../components/InlineSubscribe";
import SubmitStoryCTA from "../components/SubmitStoryCTA";
import Breadcrumbs, { breadcrumbJsonLd, categoryLabel } from "../components/Breadcrumbs";
import { splitHtmlAfterParagraph } from "../lib/splitHtml";
import { readTimeMinutes, wordCount } from "../lib/readTime";

const SITE = "https://peoplewhomatter.in";
// Comments sit far below the fold: load them as their own chunk so they never delay the article itself.
const CommentsSection = lazy(() => import("../components/CommentsSection"));
// Hero candidates; index.html preloads the same srcset/sizes as soon as the article JSON arrives, so keep these in sync.
const HERO_WIDTHS = [640, 960, 1280, 1600];
const HERO_SIZES = "(min-width: 1024px) 848px, 100vw";

const getCategoryLabel = (category) => {
  const labels = {
    people: "People Who Matter",
    companies: "Companies That Matter",
    brands: "Brands That Matter",
    ideas: "Ideas That Matter",
    initiatives: "Initiatives That Matter",
    specials: "PWM Specials",
    events: "Events That Matter",
    news: "Latest News"
  };
  return labels[category] || category;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const ArticlePage = () => {
  const { slug } = useParams();
  // If index.html's early fetch already finished, render the article (and its hero) in the very first paint.
  const [article, setArticle] = useState(() => peekEarlyData(`/articles/${slug}`));
  const preloaded = useRef(article);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(() => !article);
  const [progress, setProgress] = useState(0);
  const bodyRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const el = bodyRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight * 0.6;
      const done = Math.min(1, Math.max(0, -rect.top / Math.max(1, total)));
      setProgress(done);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [article]);

  useEffect(() => {
    const fetchArticle = async () => {
      const early = preloaded.current;
      preloaded.current = null;
      if (!early) setLoading(true);
      try {
        const articleData = early || (await getJson(`/articles/${slug}`));
        if (!early) setArticle(articleData);
        try {
          const relatedRes = await axios.get(`${API}/articles/${articleData.slug}/related?limit=6`);
          setRelatedArticles(relatedRes.data);
        } catch {
          setRelatedArticles([]);
        }
      } catch (error) {
        console.error("Error fetching article:", error);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-950 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" data-testid="article-not-found">
        <SEO title="Article Not Found" description="The article you are looking for is no longer available." noindex />
        <h1 className="font-serif text-3xl md:text-4xl font-bold mb-3">Article Not Found</h1>
        <p className="text-neutral-500 text-sm max-w-md text-center mb-6">
          This article may have been removed or the URL may be incorrect. Please browse our latest editorial coverage instead.
        </p>
        <Link to="/" className="inline-block px-6 py-3 bg-neutral-950 text-white text-sm font-semibold uppercase tracking-widest hover:bg-neutral-800 transition-colors">
          Return to Home
        </Link>
      </div>
    );
  }

  const canonicalUrl = `${SITE}/article/${article.slug}`;
  const heroImage = article.banner_image_url || article.image_url;
  const seoImage = article.og_image || heroImage;
  const seoTitle = article.meta_title || article.title;
  const seoDescription = article.meta_description || article.excerpt;
  const heroAlt = article.image_decorative ? "" : (article.image_alt || article.title);
  const minutes = readTimeMinutes(article.content);
  const crumbs = [
    { label: "Home", to: "/" },
    { label: categoryLabel(article.category), to: `/category/${article.category}` },
    { label: article.title },
  ];
  const images = [...new Set([article.banner_image_url, article.image_url, article.og_image].filter(Boolean))];
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        // NewsArticle only when the CMS explicitly marks a piece as news (future article_type flag), never by category.
        "@type": article.article_type === "news" ? "NewsArticle" : "Article",
        "@id": `${canonicalUrl}#article`,
        url: canonicalUrl,
        headline: article.title,
        description: seoDescription,
        image: images,
        thumbnailUrl: article.image_url,
        datePublished: article.published_at,
        dateModified: article.updated_at || article.published_at,
        author: { "@type": article.author?.toLowerCase().includes("team") ? "Organization" : "Person", name: article.author },
        publisher: ORGANIZATION,
        mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
        articleSection: categoryLabel(article.category),
        keywords: (article.tags || []).join(", "),
        wordCount: wordCount(article.content),
        inLanguage: "en-IN",
        isAccessibleForFree: true,
      },
      breadcrumbJsonLd(crumbs),
    ],
  };

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="article-page">
      <SEO
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        imageAlt={article.image_alt || article.title}
        url={`/article/${article.slug}`}
        type="article"
        article={article}
        jsonLd={jsonLd}
      />
      {article.slug !== slug && <Navigate to={`/article/${article.slug}`} replace />}
      <div className="fixed top-0 left-0 right-0 h-[3px] z-[60] pointer-events-none" data-testid="reading-progress">
        <div className="h-full bg-[#7A1921] transition-[width] duration-150" style={{ width: `${progress * 100}%` }} />
      </div>

      <article className="py-8 md:py-14" ref={bodyRef}>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" style={{ minWidth: 0 }}>
            <div className="lg:col-span-8 min-w-0">

              <Breadcrumbs items={crumbs} className="mb-6" />

              {/* CATEGORY / HEADLINE / DEK */}
              <header className="mb-8" data-testid="article-title-block">
                <div className="flex items-center gap-3 mb-4">
                  <Link to={`/category/${article.category}`} className="editorial-caption text-[#B89947] hover:text-wine-700 transition-colors" data-testid="article-category-link">
                    {getCategoryLabel(article.category)}
                  </Link>
                  <span className="editorial-caption text-neutral-400" aria-hidden="true">&middot;</span>
                  <span className="editorial-caption text-neutral-500" data-testid="article-editorial-type">
                    {article.editorial_type === "select" ? "PWM Select" : "PWM Original"}
                  </span>
                  {article.is_sponsored && <span className="sponsored-badge">Sponsored</span>}
                </div>
                <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-950 leading-tight tracking-tight">
                  {article.title}
                </h1>
                {article.excerpt && (
                  <p className="mt-5 text-lg md:text-xl text-neutral-700 leading-relaxed font-serif italic" data-testid="article-summary">
                    {article.excerpt}
                  </p>
                )}
              </header>

              {/* AUTHOR / DATE • READ TIME / SHARE */}
              <div className="mb-8 pb-6 border-b border-neutral-100" data-testid="byline-row">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-5 text-sm text-neutral-600">
                  <span className="inline-flex items-center gap-2">
                    <span className="text-neutral-500">By</span>
                    <span className="font-semibold text-neutral-950" data-testid="article-author">{article.author}</span>
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-neutral-400" />
                    <time dateTime={article.published_at} data-testid="article-date">{formatDate(article.published_at)}</time>
                  </span>
                  <span className="inline-flex items-center gap-2 text-neutral-500" data-testid="article-read-time">
                    <Clock className="w-4 h-4" />{minutes} min read
                  </span>
                </div>
                <ArticleShareRow title={article.title} />
              </div>

              {/* HERO IMAGE */}
              <figure className="mb-10" data-testid="article-hero">
                <div className="relative w-full aspect-[16/9] bg-neutral-100 overflow-hidden">
                  <LeadPicture
                    src={heroImage}
                    widths={HERO_WIDTHS}
                    sizes={HERO_SIZES}
                    alt={heroAlt}
                    width="1600"
                    height="900"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                {article.ai_generated_visual && (
                  <figcaption className="mt-2 text-[11px] tracking-wide text-neutral-400" data-testid="ai-visual-disclosure">AI generated visual</figcaption>
                )}
              </figure>

              {article.video_url && (
                <div className="video-wrapper mb-12">
                  <iframe
                    src={article.video_url}
                    title={article.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="border-0"
                  ></iframe>
                </div>
              )}

              {article.editorial_type === "select" && (
                <p className="mb-8 pt-4 border-t border-neutral-100 text-sm italic text-neutral-500 leading-relaxed" data-testid="pwm-select-disclosure">
                  <span className="not-italic font-medium tracking-wider uppercase text-xs text-neutral-500">PWM Select</span>
                  {" "}&mdash; Based on material submitted to People Who Matter and reviewed by our editorial team.
                </p>
              )}

              {/* ARTICLE BODY (long reads get a compact subscribe card after the 4th paragraph) */}
              {(() => {
                const [head, tail] = splitHtmlAfterParagraph(article.content, 4, 8);
                return tail ? (
                  <>
                    <ArticleBody html={head} testId="article-body" />
                    <InlineSubscribe variant="inline" />
                    <ArticleBody html={tail} testId="article-body-continued" />
                  </>
                ) : <ArticleBody html={head} />;
              })()}

              <BannerSlot slot="article-inline" houseFallback={false} />

              {article.tags && article.tags.length > 0 && (
                <div className="mt-12 pt-8 border-t border-neutral-100">
                  <h2 className="editorial-caption mb-4">Tags</h2>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <span key={tag} className="category-pill">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-12 pt-8 border-t border-neutral-100" data-testid="end-share-row">
                <p className="editorial-caption text-neutral-500 mb-4 text-center">Enjoyed this story? Share it</p>
                <ArticleShareRow title={article.title} align="center" />
              </div>

              {/* READ NEXT */}
              <ReadNext articles={relatedArticles} currentSlug={article.slug} />

              <SubmitStoryCTA variant="article" />

              <BannerSlot slot="article-footer" />

              <div className="mt-10 p-8 md:p-10 bg-neutral-950 text-white text-center" data-testid="subscribe-cta">
                <h3 className="font-serif text-2xl md:text-3xl font-bold mb-3">
                  Stories worth telling, in your inbox.
                </h3>
                <p className="text-neutral-400 mb-6 max-w-md mx-auto text-sm">
                  Get the latest People Who Matter features delivered weekly.
                </p>
                <Link
                  to="/#newsletter"
                  className="inline-block px-8 py-3 bg-white text-neutral-950 text-sm font-semibold tracking-wider uppercase hover:bg-neutral-200 transition-colors"
                  data-testid="subscribe-now-cta"
                >
                  Subscribe Now
                </Link>
              </div>

              <Suspense fallback={null}>
                <CommentsSection articleSlug={article.slug} />
              </Suspense>
            </div>

            {/* Sidebar shows the recommendations not already used in Read Next */}
            <ArticleSidebar relatedArticles={relatedArticles.slice(3)} />
          </div>
        </div>
      </article>
    </div>
  );
};

export default ArticlePage;
